import { Button, Col, Divider, Pagination as Paging, Row, Table, Typography } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { PageHeader } from '../../../components/layout/PageHeader';
import {
  changeDatasetSettings,
  commit,
  deleteDatasetFileVersion,
  deleteMultipleDatasetFileVersions,
  scorePreviousDatasetVersion,
} from '../../../lib/api/dataset-api';
import { useInterval } from '../../../lib/useInterval';
import { userIsAdmin } from '../../../lib/util';
import { anyRecentProcessingDatasetFileVersion } from '../../../lib/util/processing';
import { DatasetFileVersion, DatasetSettingsData, DatasetFilters } from '../../../types/dataset';
import { Pagination } from '../../../types';
import { useAuthenticationContext } from '../../authentication/AuthenticationContext';
import { getDatasetOverviewTableColumns } from './DatasetOverviewTable';
import DatasetSettings from './DatasetSettings';
import DocumentUpload from './DocumentUpload';
import { Icon } from '@ant-design/compatible';
import { useGetDatasetFiles, useGetModelVersion, useGetProjectAndDataset } from '../../../lib/hooks';
import { Spinner } from '../../../components/loading/Spinner';
import { initialFilterValues, renderFilterString, setFiltersOnMount } from '../Filters';
import { ErrorText } from '../../../components/text/ErrorText';

const { Text } = Typography;

const defaultPaging = {
  pageSize: 10,
  page: 1,
};

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const DatasetOverview = () => {
  let history = useHistory();
  const location = useLocation();
  const query = useQuery();

  const { datasetId, projectId } = useParams() as { datasetId: string; projectId: string };

  const { currentUser } = useAuthenticationContext();

  const [queryString, setQueryString] = useState<string>('');
  const [paginationObject, setPaginationObject] = useState<Pagination>(defaultPaging);
  const [filters, setFilters] = useState<DatasetFilters>(initialFilterValues);
  const [commiting, setCommiting] = useState<boolean>(false);

  const { project, dataset, datasetVersion, refetch } = useGetProjectAndDataset(projectId, datasetId);

  const { datasetFiles, filterItemsCount, filesLoading, refetchFiles, filesError } = useGetDatasetFiles(
    datasetVersion?.version_id || '',
    queryString,
    paginationObject
  );

  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [settingsVisible, setSettingsVisible] = useState<boolean>(false);
  const { modelId } = useGetModelVersion(datasetVersion!);

  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const rowSelection = {
    selectedRowKeys: selectedItems,
    getCheckboxProps: (record: DatasetFileVersion) => ({
      id: record.id,
    }),
    onChange: (selectedRowKeys: any, selectedRows: any) => {
      setSelectedItems(selectedRowKeys);
    },
  };

  useInterval(async () => {
    if (!dataset) return;

    try {
      if (anyRecentProcessingDatasetFileVersion(datasetFiles)) {
        refetchFiles(false);
      }
    } catch (e) {}
  }, 2000);

  //ONMOUNT: get the query parameters and set filters on these values
  useEffect(() => {
    setFiltersOnMount(query, setFilters, setPaginationObject);

    if (query.get('page')) {
      setPaginationObject({
        pageSize: defaultPaging.pageSize,
        page: query.get('page') ? parseInt(query.get('page')!) : defaultPaging.page,
      });
    }
    //we dont want query in dependency arry => infinite loop:
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkPagesAfterFilter = useCallback(() => {
    const pageMinCapacity = (paginationObject.page - 1) * paginationObject.pageSize;
    if (filterItemsCount > 0 && filterItemsCount < pageMinCapacity) {
      setPaginationObject(prev => ({ ...prev, page: 1 }));
    }
  }, [filterItemsCount, paginationObject]);

  //set filterquerystring whenever filter updates
  useEffect(() => {
    setQueryString(renderFilterString(filters));
    checkPagesAfterFilter();
  }, [filters, checkPagesAfterFilter]);

  useEffect(() => {
    if (datasetId) {
      history.replace({
        pathname: location.pathname,
        search: 'page=' + paginationObject.page + '&size=' + paginationObject.pageSize + queryString,
      });
      refetch();
    }
  }, [datasetId, history, location.pathname, queryString, paginationObject, refetch]);

  const handleTableChange = (x: any, y: any, sorter: any) => {
    if (Array.isArray(sorter)) {
      setFilters(prev => ({
        ...prev,
        sort: sorter.map(s => ({ field: s.field, order: s.order })),
      }));
    } else {
      if (sorter.column === undefined) {
        setFilters(prev => ({ ...prev, sort: [{ field: 'time_created', order: 'descend' }] }));
        return;
      }
      setFilters(prev => ({
        ...prev,
        sort: [{ field: sorter.field, order: sorter.order }],
      }));
    }
  };

  const noFiltersActivated = () => {
    return !filters.tag && !filters.status.length && !filters.date.length;
  };

  const onClickUrl = useCallback(
    (record: DatasetFileVersion) => {
      return `/projects/${projectId}/datasets/${datasetId}/dataset-file/${record.id}?page=${paginationObject.page}${queryString}`;
    },
    [projectId, datasetId, paginationObject, queryString]
  );

  const onSave = async (settings: DatasetSettingsData) => {
    if (datasetVersion) {
      changeDatasetSettings(settings, datasetVersion.version_id);
      refetch();
    }
  };

  const onDelete = async (record: DatasetFileVersion) => {
    await deleteDatasetFileVersion(record);
    refetchFiles(true);
    refetch();
  };

  const onDeleteSelection = async (records: string[]) => {
    await deleteMultipleDatasetFileVersions(records);
    refetchFiles(true);
    refetch();
    setSelectedItems([]);
  };

  const onDocumentUploaded = () => {
    refetchFiles(true);
    refetch();
  };

  const commitDataset = async () => {
    if (datasetVersion) {
      setCommiting(true);
      await commit(datasetVersion.version_id);
      setCommiting(false);
      await scorePreviousDatasetVersion(datasetVersion.version_id);
    }
    refetch();
  };

  return (
    <>
      <PageHeader
        back={{ url: `/projects/${projectId}/datasets/${datasetId}`, label: project?.name || '' }}
        header={(project ? project.name + ': ' : '') + (dataset ? dataset.name : '')}
        description="Click on a file to annotate."
      />
      <Row className="items-center my-3">
        <Col span={12}>
          {!noFiltersActivated() ? (
            <>
              <Text>
                Filtered {filterItemsCount} of {dataset && dataset.latestVersion.document_count} Results
              </Text>
              <Divider type="vertical" orientation="center" className="mx-3" />
              <Button onClick={() => setFilters(initialFilterValues)} type="link" className="pl-0">
                Reset all filters
                <Icon type="close" />
              </Button>
            </>
          ) : (
            <Text>{dataset && dataset.latestVersion.document_count} Results</Text>
          )}
          {selectedItems.length ? (
            <Button type="primary" size="large" className="ml-3" onClick={() => onDeleteSelection(selectedItems)}>
              Delete selection
            </Button>
          ) : null}
        </Col>
        <Col span={12} className="flex justify-end">
          <Button
            type="ghost"
            size="large"
            className="mr-3"
            onClick={() => {
              history.push(`/projects/${projectId}/datasets/${datasetId}/report`);
            }}>
            See report
          </Button>
          <Button
            onClick={() => setSettingsVisible(true)}
            disabled={!userIsAdmin(currentUser)}
            type="primary"
            size={'large'}
            className="mr-3">
            Settings
          </Button>
          <Button onClick={() => setModalVisible(true)} type="primary" size={'large'}>
            Upload Documents
          </Button>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          {filesError ? (
            <ErrorText>Something went wrong. We could not find the documents you were looking for.</ErrorText>
          ) : (
            <>
              <Table
                className="ant-table-toolbox"
                columns={getDatasetOverviewTableColumns(onClickUrl, onDelete, filters, setFilters)}
                rowKey={record => record.id}
                dataSource={datasetFiles}
                loading={{
                  spinning: filesLoading || commiting,
                  indicator: (
                    <Row className="flex justify-center items-center">
                      <Spinner />
                      <p style={{ fontSize: '14px', marginTop: '2em', fontWeight: 'bold' }}>
                        {commiting && 'Commiting...'}
                      </p>
                    </Row>
                  ),
                }}
                pagination={false}
                rowSelection={rowSelection}
                onChange={handleTableChange as any}
              />
              <div className="flex justify-end my-4">
                <Paging
                  showSizeChanger={false}
                  defaultCurrent={1}
                  current={paginationObject.page}
                  pageSize={defaultPaging.pageSize}
                  total={filterItemsCount}
                  onChange={page => setPaginationObject({ ...defaultPaging, page: page })}
                />
              </div>
            </>
          )}
        </Col>
      </Row>
      <DocumentUpload
        title={dataset ? "Upload to dataset '" + dataset.name + "'" : ''}
        visible={modalVisible}
        modelId={modelId}
        datasetVersion={datasetVersion ? datasetVersion.version_id : ''}
        onDocumentUploaded={onDocumentUploaded}
        onClose={() => setModalVisible(false)}
      />
      <DatasetSettings
        title="Dataset Settings"
        visible={settingsVisible}
        datasetVersion={datasetVersion ? datasetVersion : null}
        onClose={() => setSettingsVisible(false)}
        onSave={(settings: DatasetSettingsData) => {
          onSave(settings);
          setSettingsVisible(false);
        }}
        onCommit={() => {
          commitDataset();
          setSettingsVisible(false);
        }}
        commiting={commiting}
      />
    </>
  );
};

export default DatasetOverview;
